import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "camera", "names", "chips"]

  connect () {
    this.known = Array.from(this.inputTarget.files)
  }

  capture () {
    this.merge(Array.from(this.cameraTarget.files))
    this.cameraTarget.value = ""
  }

  merge (added) {
    const files = this.inputTarget.multiple ? this.known.concat(added.filter((file) => !this.knows(file))) : added
    this.inputTarget.files = this.buildFileList(files)
    this.render()
  }

  knows (file) {
    return this.known.some((other) => other.name === file.name && other.size === file.size && other.lastModified === file.lastModified)
  }

  update (event) {
    if (event && event.target === this.inputTarget) {
      this.merge(Array.from(this.inputTarget.files))
      return
    }

    this.render()
  }

  render () {
    const files = Array.from(this.inputTarget.files)
    this.known = files

    if (this.hasChipsTarget) {
      this.renderChips(files)
    } else if (this.hasNamesTarget) {
      this.namesTarget.textContent = files.map((file) => file.name).join(", ")
    }
  }

  renderChips (files) {
    this.chipsTarget.innerHTML = ""

    files.forEach((file, index) => {
      const chip = document.createElement("div")
      chip.className = "flex items-center gap-2 rounded-xl border-2 border-gray-100 bg-gray-50 px-3 py-2 text-xs"

      const name = document.createElement("span")
      name.className = "font-semibold text-gray-700"
      name.textContent = file.name

      const size = document.createElement("span")
      size.className = "text-gray-400"
      size.textContent = this.humanSize(file.size)

      const remove = document.createElement("span")
      remove.className = "cursor-pointer text-gray-400 hover:text-gray-600"
      remove.textContent = "✕"
      remove.addEventListener("click", () => this.removeFile(index))

      chip.append(name, size, remove)
      this.chipsTarget.append(chip)
    })
  }

  humanSize (bytes) {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    }

    return `${Math.round(bytes / 1024)} KB`
  }

  removeFile (index) {
    const files = Array.from(this.inputTarget.files)
    files.splice(index, 1)
    this.inputTarget.files = this.buildFileList(files)
    this.render()
  }

  over (event) {
    event.preventDefault()
  }

  drop (event) {
    event.preventDefault()

    this.merge(Array.from(event.dataTransfer.files).filter((file) => this.accepted(file)))
  }

  accepted (file) {
    const accept = this.inputTarget.accept

    if (!accept) return true

    return accept.split(",").map((rule) => rule.trim()).some((rule) => {
      if (rule.startsWith(".")) {
        return file.name.toLowerCase().endsWith(rule.toLowerCase())
      }

      if (rule.endsWith("/*")) {
        return file.type.startsWith(rule.slice(0, -1))
      }

      return file.type === rule
    })
  }

  buildFileList (files) {
    const transfer = new DataTransfer()
    files.forEach((file) => transfer.items.add(file))
    return transfer.files
  }
}
